<template>
  <!-- Existing client warning -->
  <DefaultAlert
    v-if="existingClientSelected"
    text="Jūs pasirinkote egzistuojantį klientą, pakorgavus jo duomenis, informacija bus atnaujinta ir kliento profilyje."
    alertClass="alert-warning"
    :dismissible="false"
    @dismiss="existingClientSelected = false"
    class="mb-4"
  />

  <!-- Passenger Form -->
  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
    <!-- Name Input -->
    <div>
      <span class="flex items-center mb-2">Vardas*</span>
      <input
        v-model="form.firstName"
        type="text"
        placeholder="Vardenis"
        class="input w-full"
      />
    </div>

    <!-- Surname Input -->
    <div>
      <span class="flex items-center mb-2">Pavardė*</span>
      <input
        v-model="form.lastName"
        type="text"
        placeholder="Pavardenis"
        class="input w-full"
      />
    </div>

    <!-- Phone Input -->
    <div>
      <span class="flex items-center mb-2">Telefonas*</span>
      <div class="relative">
        <span
          class="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/60 z-10"
          >+370</span
        >
        <input
          :value="form.phone?.replace('+370', '')"
          @input="(e) => (form.phone = '+370' + e.target.value)"
          placeholder="61111111"
          type="text"
          maxlength="8"
          class="input w-full pl-14"
        />
      </div>
    </div>

    <!-- Email Input -->
    <div>
      <span class="flex items-center mb-2">El. paštas</span>
      <input
        v-model="form.email"
        type="email"
        placeholder="vardenis.p@pastas.lt"
        class="input w-full"
      />
    </div>

    <!-- Address Input -->
    <div>
      <span class="flex items-center mb-2">Adresas</span>
      <AddressSearchInput
        v-model="form.address"
        :required="false"
        label=""
        class="w-full"
      />
    </div>

    <!-- Valid until input -->
    <div>
      <span class="flex items-center mb-2">Paslauga galioja iki</span>
      <input
        v-model="form.valid_until"
        type="date"
        class="input w-full md:w-56"
      />
    </div>

    <!-- Service type dropdown -->
    <div>
      <span class="flex items-center mb-2">Paslaugų teikimo pagrindas</span>
      <select v-model="form.service_type" class="select w-full">
        <option :value="null">— nepasirinkta —</option>
        <option
          v-for="(config, key) in referenceDataStore.clientServiceTypeConfig"
          :key="key"
          :value="key"
        >
          {{ config.text }}
        </option>
      </select>
    </div>
  </div>

  <div class="grid grid-cols-1 gap-4 mb-6">
    <!--Additional info -->
    <div>
      <span class="flex items-center mb-2">Keleivio pastaba</span>
      <textarea
        v-model="form.description"
        placeholder="Pastabos tekstas"
        class="textarea w-full"
      />
    </div>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-5 gap-2">
    <!-- Disabled Checkbox -->
    <div class="flex items-center gap-2">
      <input v-model="form.disabled" type="checkbox" class="checkbox" />
      <span class="">Neįgaliojo vežimėlyje?</span>
    </div>

    <!-- Assistant Needed Checkbox -->
    <div class="flex items-center gap-2">
      <input v-model="form.assistantNeeded" type="checkbox" class="checkbox" />
      <span class="">Reikalingas palydėjimas?</span>
      <div
        class="lg:tooltip"
        data-tip="Ar reikalingas keleivio palydėjimas nuo namų iki automobilio?"
      >
        <DefaultIcon name="info" />
      </div>
    </div>

    <!-- Assistant person -->
    <div class="flex items-center gap-2">
      <input
        v-model="form.assistantPersons"
        :true-value="1"
        :false-value="0"
        type="checkbox"
        class="checkbox"
      />
      <span class="">Lydintis asmuo?</span>
      <div class="lg:tooltip" data-tip="Ar keleivis bus lydimas kito asmens?">
        <DefaultIcon name="info" />
      </div>
    </div>

    <!-- Child seat needed -->
    <div class="flex items-center gap-2">
      <input
        v-model="form.child_seat_needed"
        type="checkbox"
        class="checkbox"
      />
      <span class="">Reikalinga vaikiška kėdutė?</span>
    </div>

    <!-- Wheelchair needed -->
    <div class="flex items-center gap-2">
      <input
        v-model="form.wheelchair_needed"
        type="checkbox"
        class="checkbox"
      />
      <span class="">Reikalingas neįgaliojo vežimėlis?</span>
    </div>
  </div>

  <!-- Buttons Section -->
  <div class="flex gap-4 mt-6">
    <button @click="addPassenger" class="btn btn-primary">
      Įtraukti keleivį
    </button>
    <button @click="findPassenger" class="btn" :disabled="clientSearching">
      {{ clientSearching ? "Ieškoma..." : "Ieškoti" }}
    </button>
    <button
      v-if="existingClientSelected"
      @click="resetForm"
      class="btn btn-ghost"
    >
      Išvalyti
    </button>
  </div>

  <!-- Search results section -->
  <div v-if="clientSearchResults.length > 0" class="mt-6">
    <h3 class="font-semibold text-base-content mb-2">Paieškos rezultatai:</h3>
    <div class="space-y-2">
      <div
        v-for="result in clientSearchResults"
        :key="result.id"
        class="p-2 border rounded-lg hover:bg-base-200"
      >
        <div class="flex justify-between items-center gap-3">
          <DefaultIcon
            @click="selectPassenger(result)"
            name="plus"
            size="w-5 h-5"
            class="btn btn-circle btn-sm btn-ghost text-primary"
            title="Pasirinkti keleivį"
          />
          <div class="flex-1">
            <div class="flex items-baseline gap-3">
              <span class="font-medium"
                >{{ result.name }} {{ result.surname }}</span
              >
              <span class="text-sm text-base-content/70">{{ result.phone }}</span>
            </div>
            <div class="flex items-baseline gap-1 text-sm text-base-content/70 mt-1">
              <span v-if="result.email">{{ result.email }}, </span>
              <span v-if="result.address">{{ result.address }}, </span>
            </div>
            <div class="flex items-baseline gap-1 text-sm text-base-content/70 mt-1">
              <span v-if="result.valid_until">
                Galioja iki: {{ result.valid_until }}.
              </span>
              <span v-if="result.service_type">
                Paslaugų teikimo pagrindas:
                {{
                  referenceDataStore.clientServiceTypeConfig[
                    result.service_type
                  ]?.text
                }}.
              </span>
              <span>
                Pavėžėjimų (viso/neatvyko):
                {{ result.trips_count }}/{{ result.trips_count_not_arrived }}.
              </span>
              <span v-if="result.service_restricted" class="text-error">
                Apribotas paslaugų teikimas iki:
                {{ result.service_restriction_until }}.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineEmits, onMounted } from "vue";
import DefaultIcon from "@/components/icons/DefaultIcon.vue";
import DefaultAlert from "@/components/ui/alerts/DefaultAlert.vue";
import { useClientStore } from "@/stores/clients";
import { useReferenceDataStore } from "@/stores/referenceData";
import AddressSearchInput from "@/components/search/AddressSearchInput.vue";

const clientStore = useClientStore();
const referenceDataStore = useReferenceDataStore();

onMounted(async () => {
  await referenceDataStore.ensureDataLoaded();
});
const clientSearchResults = ref([]);
const clientSearching = ref(false);
const existingClientSelected = ref(false);

// Emits to parent
const emit = defineEmits(["updatePassengers"]);

// Reactive form state
const form = ref({
  id: null, // Null indicates a new passenger
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  assistantNeeded: false,
  child_seat_needed: false,
  wheelchair_needed: false,
  assistantPersons: 0,
  disabled: false,
  valid_until: null,
  service_type: null,
  service_restricted: false,
  service_restriction_until: null,
  description: "",
});

// Passenger list
const passengers = ref([]);

// Add passenger to the list
const addPassenger = () => {
  if (form.value.firstName && form.value.lastName && form.value.phone) {
    passengers.value.push({ ...form.value });
    emitPassengers(); // Emit updated list
    resetForm();
  } else {
    alert("Vardas, Pavardė ir Telefono numeris yra būtini laukai.");
  }
};

// Emit passenger list to the parent automatically
const emitPassengers = () => {
  emit("updatePassengers", passengers.value);
};

// Reset form fields
const resetForm = () => {
  form.value = {
    id: null,
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    assistantNeeded: false,
    child_seat_needed: false,
    wheelchair_needed: false,
    disabled: false,
    valid_until: null,
    service_type: null,
    description: "",
  };
  passengers.value = [];
  existingClientSelected.value = false;
};

/*
  Search functionality
*/

const findPassenger = async () => {
  clientSearching.value = true;

  try {
    // Create search params object with non-empty values only
    const searchParams = {};
    if (form.value.firstName) searchParams.name = form.value.firstName;
    if (form.value.lastName) searchParams.surname = form.value.lastName;
    if (form.value.phone) searchParams.phone = form.value.phone;
    if (form.value.email) searchParams.email = form.value.email;

    // Only search if at least one parameter is provided
    if (Object.keys(searchParams).length > 0) {
      const results = await clientStore.searchClients(searchParams);
      if (results.length > 0) {
        clientSearchResults.value = results;
      } else {
        alert("Pagal nurodytą keleivio informaciją nepavyko nieko rasti");
      }
    } else {
      alert("Įveskite bent vieną paieškos kriterijų");
    }
  } catch (error) {
    alert(error || "Įvyko klaida ieškant keleivio");
  } finally {
    clientSearching.value = false;
  }
};

// Select passenger and fill the form
const selectPassenger = (passenger) => {
  // Fill the form with passenger data
  form.value = {
    id: passenger.id,
    firstName: passenger.name,
    lastName: passenger.surname,
    phone: passenger.phone,
    email: passenger.email,
    address: passenger.address,
    valid_until: passenger.valid_until,
    service_type: passenger.service_type,
    service_restricted: passenger.service_restricted,
    service_restriction_until: passenger.service_restriction_until,
    disabled: passenger.disabled,
    child_seat_needed: passenger.child_seat_needed,
    wheelchair_needed: passenger.wheelchair_needed,
    description: passenger.description,
  };

  // Clear search results after selection
  clientSearchResults.value = [];
  existingClientSelected.value = true;
};
</script>

<style scoped></style>
